// Tracked-changes diff for the document canvas. Paragraph-level LCS first
// (cheap even for long documents), then word-level LCS inside each
// changed paragraph pair so reviewers see exactly what moved.
// Output: [{ type: "same" | "add" | "del", text }], adjacent runs merged.

const MAX_CELLS = 2_000_000; // LCS table guard (~2M cells)

function lcsOps(a, b, eq = (x, y) => x === y) {
  const n = a.length, m = b.length;
  if ((n + 1) * (m + 1) > MAX_CELLS) return null;
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = eq(a[i], b[j]) ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (eq(a[i], b[j])) { ops.push(["same", a[i]]); i++; j++; }
    else if (dp[i + 1][j] >= dp[i][j + 1]) { ops.push(["del", a[i]]); i++; }
    else { ops.push(["add", b[j]]); j++; }
  }
  while (i < n) ops.push(["del", a[i++]]);
  while (j < m) ops.push(["add", b[j++]]);
  return ops;
}

function merge(parts) {
  const out = [];
  for (const p of parts) {
    if (!p.text) continue;
    const last = out[out.length - 1];
    if (last && last.type === p.type) last.text += p.text;
    else out.push({ ...p });
  }
  return out;
}

export function tokenizeWords(s) {
  return String(s || "").match(/\s+|[^\s]+/g) || [];
}

export function diffWords(a, b) {
  const ops = lcsOps(tokenizeWords(a), tokenizeWords(b));
  if (!ops) return merge([{ type: "del", text: a }, { type: "add", text: b }]);
  return merge(ops.map(([type, text]) => ({ type, text })));
}

export function splitParagraphs(s) {
  // Keep the separators attached so the rejoined text is byte-identical.
  return String(s || "").split(/(?<=\n\n)/);
}

export function diffDocs(a, b) {
  const pa = splitParagraphs(a), pb = splitParagraphs(b);
  const ops = lcsOps(pa, pb);
  if (!ops) return diffWords(a, b);
  const out = [];
  for (let k = 0; k < ops.length; k++) {
    const [type, text] = ops[k];
    if (type === "same") { out.push({ type, text }); continue; }
    // Collect a run of dels/adds and word-diff them pairwise.
    const dels = [], adds = [];
    while (k < ops.length && ops[k][0] !== "same") {
      (ops[k][0] === "del" ? dels : adds).push(ops[k][1]);
      k++;
    }
    k--;
    const pairs = Math.min(dels.length, adds.length);
    for (let p = 0; p < pairs; p++) out.push(...diffWords(dels[p], adds[p]));
    for (let p = pairs; p < dels.length; p++) out.push({ type: "del", text: dels[p] });
    for (let p = pairs; p < adds.length; p++) out.push({ type: "add", text: adds[p] });
  }
  return merge(out);
}

export function diffStats(parts) {
  const words = (t) => (String(t).match(/\S+/g) || []).length;
  let added = 0, removed = 0;
  for (const p of parts) {
    if (p.type === "add") added += words(p.text);
    if (p.type === "del") removed += words(p.text);
  }
  return { added, removed };
}

// Models sometimes wrap a returned document in ``` fences or add a
// one-line preamble; strip both.
export function cleanModelDocument(text) {
  let s = String(text || "").trim();
  const fence = s.match(/^```(?:markdown|md)?\s*\n([\s\S]*?)\n```$/i);
  if (fence) s = fence[1].trim();
  s = s.replace(/^(?:here(?:'s| is) (?:the|your) (?:revised|updated|rewritten)[^\n]*:\s*\n+)/i, "");
  return s;
}

export function replaceRange(full, start, end, replacement) {
  const s = String(full || "");
  return s.slice(0, start) + replacement + s.slice(end);
}

export const QUICK_EDITS = [
  { label: "Shorter", instruction: "Make it about half as long while keeping every key point." },
  { label: "Longer", instruction: "Expand it with more detail, examples and explanation." },
  { label: "More formal", instruction: "Rewrite in a more formal, professional tone." },
  { label: "Simpler", instruction: "Rewrite in plain, simple language a non-expert can follow." },
  { label: "Fix grammar", instruction: "Fix spelling, grammar and punctuation only. Do not change meaning or style." },
  { label: "Bullets", instruction: "Restructure it as concise bullet points." },
];

// Documents imported from Word / PowerPoint / PDF carry their pictures as
// Markdown image lines; edits must not drop or move them.
export const KEEP_PICTURES = "Keep every picture line (![...](...)) exactly as it is and where it is, unless the instruction is about the pictures.";

export function buildSelectionPrompt(doc, selected, instruction) {
  return "Here is a document in Markdown:\n\n<document>\n" + doc + "\n</document>\n\n" +
    "Rewrite ONLY this excerpt from it:\n\n<excerpt>\n" + selected + "\n</excerpt>\n\n" +
    "Instruction: " + instruction + "\n\n" +
    "Return ONLY the replacement text for the excerpt — no tags, no quotes, no commentary. Keep it consistent with the surrounding document. " + KEEP_PICTURES;
}

export function buildDocPrompt(doc, instruction) {
  return "Here is a draft document in Markdown:\n\n" + doc + "\n\nRevise it according to this instruction: " + instruction +
    "\n\nReturn ONLY the complete revised document in Markdown — no preamble, no commentary. " + KEEP_PICTURES;
}
